/**
 * NeuronResponsiveTabs.tsx
 *
 * Responsive tab component that adapts to mobile and desktop layouts.
 */
import {
  Box,
  MenuItem,
  Select,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getResponsiveTabsStyles } from "../styles/component/NeuronResponsiveTabs.styles";
type TabItem = {
  label: string;
  content?: ReactNode;
  onSelect?: () => void;
};
type ResponsiveTabsProps = {
  items: TabItem[];
  value?: number; // controlled
  initialTab?: number; // uncontrolled
  centered?: boolean;
  fullWidthSelect?: boolean;
  onTabChange?: (index: number) => void;
};
/**
 * NeuronResponsiveTabs
 * Renders responsive tabs or a select dropdown based on screen size.
 */
const NeuronResponsiveTabs = ({
  items,
  value,
  initialTab = 0,
  centered = true,
  fullWidthSelect = true,
  onTabChange,
}: ResponsiveTabsProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Determine if controlled
  const isControlled = value !== undefined;
  // Internal state for uncontrolled
  const [activeTab, setActiveTab] = useState(() => {
    // Clamp initialTab to valid range
    if (initialTab < 0 || initialTab >= items.length) return -1;
    return initialTab;
  });

  // Sync with initialTab prop changes (uncontrolled)
  useEffect(() => {
    if (!isControlled) {
      if (initialTab < 0 || initialTab >= items.length) {
        setActiveTab(-1);
      } else {
        setActiveTab(initialTab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, items.length]);

  // Clamp value for controlled, or use internal state
  const currentTab = isControlled
    ? value! < 0 || value! >= items.length
      ? -1
      : value!
    : activeTab;

  const styles = useMemo(
    () => getResponsiveTabsStyles(theme, currentTab),
    [theme, currentTab]
  );

  const handleTabChange = useCallback(
    (_: any, index: number) => {
      items[index]?.onSelect?.();
      if (!isControlled) setActiveTab(index);
      onTabChange?.(index);
    },
    [items, isControlled, onTabChange]
  );

  const menuItems = useMemo(
    () =>
      items?.map((item, index) => (
        <MenuItem key={item.label + index} value={index}>
          {item.label}
        </MenuItem>
      )),
    [items]
  );

  const tabElements = useMemo(
    () =>
      items?.map((item, index) => (
        <Tab
          key={item.label + index}
          label={item.label}
          sx={styles.tab(index)}
        />
      )),
    [items, styles]
  );

  const handleSelectChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      handleTabChange(null, Number(e.target.value));
    },
    [handleTabChange]
  );

  return (
    <Box sx={styles.container}>
      {isMobile ? (
        <Box sx={{ ...styles.selectWrapper, width: "50vw" }}>
          <Select
            fullWidth={fullWidthSelect}
            value={currentTab === -1 ? "" : currentTab}
            onChange={handleSelectChange}
            displayEmpty
            sx={styles.select}
            data-testid="select"
            data-fullwidth={fullWidthSelect.toString()}
          >
            {menuItems}
          </Select>
        </Box>
      ) : (
        <Tabs
          value={currentTab === -1 ? false : currentTab}
          onChange={handleTabChange}
          textColor="inherit"
          TabIndicatorProps={{ style: { display: "none" } }}
          centered={centered}
          sx={styles.tabs}
        >
          {tabElements}
        </Tabs>
      )}
      <Box sx={styles.tabContent}>
        {currentTab === -1 ? null : items[currentTab]?.content ?? null}
      </Box>
    </Box>
  );
};
export default React.memo(NeuronResponsiveTabs);
